import React, {useContext, useState} from 'react';
import {Modal} from "react-bootstrap";
import IframeWidget from "../../../../utils/IframeWidget";
import StoreUtils from "../../../../services/Store/StoreUtils";
import {useLocation, useNavigate} from "react-router-dom";
import {usePrefix} from "../../../../services/Prefix";
import {ConfigApi} from "../../../../services/Api/types";
import {VoucherProductsContext} from "../../../../services/VoucherProducts/VoucherProductsContext";
import MenuProductModalHeader from "./MenuProductModalHeader";
import ResponsiveComponent from "../../../../components/Common/ResponsiveComponent";
import MenuProductImages from "./MenuProductImages";
import StateItemHelper from "../../../../services/Menu/services/State/StateItemHelper";
import {RootState} from "../../../../../reducers";
import {
    getAvailabilities,
    getItemGroups,
    getItems,
    getModifierGroups,
    getPromotions
} from "../../../../services/Menu/reducer";
import {connect} from "react-redux";
import {getSession} from "../../../../services/Session/reducer";
import PromotionHelper from "../../../../services/Menu/services/Promotion/PromotionHelper";
import {getCart} from "../../../../services/Cart/reducer";
import ProductContent from "./ProductContent/ProductContent";
import MenuEditItemViewHelper from "../../../../services/Menu/services/ItemView/MenuEditItemViewHelper";
import MenuProductModalFooter from "./MenuProductModalFooter";
import handleException from "../../../../services/Api/handleException";
import {useCart} from "../../../../services/Cart/cartService";
import AppConfig from "../../../../services/AppConfig";

interface OwnProps {
    show: boolean;
    setShow: React.Dispatch<React.SetStateAction<boolean>>;
    config: ConfigApi | undefined;
    item: Record<string, any>;
    itemGroup: Record<string, any> | undefined;
    nested?: true;
    customOnSubmit?: (state: Record<string, any>) => void;
    externalItemState?: Record<string, any>;
    preventRemovePromotionOnHide?: boolean;
    onHide?: (itemState?: Record<string, any>) => void;
    orderItemId?: number;
    preventGoBack?: true;
}

type Props = ReturnType<typeof mapStateToProps> & OwnProps;

const MenuProductModal = ({
                              show,
                              setShow,
                              config,
                              item,
                              itemGroup,
                              items,
                              modifierGroups,
                              session,
                              promotions,
                              cart,
                              nested,
                              customOnSubmit,
                              externalItemState,
                              availabilities,
                              preventRemovePromotionOnHide,
                              onHide,
                              orderItemId,
                              preventGoBack
                          }: Props) => {
    const location = useLocation();
    const navigate = useNavigate();
    const prefix = usePrefix();
    const {handleOnPromotionLimit, handleRemovePromotion} = useContext(VoucherProductsContext);
    const [itemState, setItemState] = useState<Record<string, any>>(externalItemState ?? StateItemHelper.create(item.id, items, modifierGroups, session.order_type));
    const productView = MenuEditItemViewHelper.getItemView(items, modifierGroups, availabilities, itemState.item_id, undefined, undefined, 1, session.pickup_at, itemState, session.order_type);
    const [isItemAvailable, setIsItemAvailable] = useState(true);
    const [errors, setErrors] = useState<any[]>([]);
    const cartService = useCart();
    const [loading, setLoading] = useState(false);

    const handleNavigateFromModal = () => {
        if (preventGoBack) return;

        if (location.state && location.state?.back) {
            if (IframeWidget.isInit()) {
                navigate(StoreUtils.isHomepageMenu(config) ? '/' : "/menu");
            } else {
                navigate(-1);
            }
        } else {
            navigate(AppConfig.getLinkWithPrefix(StoreUtils.isHomepageMenu(config) ? `/` : '/menu', prefix), {
                replace: true,
                state: {action: 'stay'}
            });
        }
    };

    const handleAddProductToCart = async () => {
        setLoading(true);
        setErrors([]);

        try {

            if (orderItemId) {
                itemState.id = orderItemId;
            }
            await cartService.addItem(itemState);

            const isItemInCart = itemState?.item_id === item.id;

            if (location.state?.promotionId && !isItemInCart && !preventRemovePromotionOnHide) {
                await handleRemovePromotion();
            }

            if (location.state?.fromVoucher) {
                handleOnPromotionLimit(itemState);
            }

            IframeWidget.closeModal();
            setShow(false);
            handleNavigateFromModal();
        } catch (resError: any) {
            setErrors(handleException(resError));
        }
        setLoading(false);
    };

    const handleSubmit = async () => {
        if (nested) {
            IframeWidget.closeModal();
            setShow(false);
            customOnSubmit?.(itemState);
        } else {
            await handleAddProductToCart();
        }
    }

    const handleHideModal = async () => {
        onHide?.(itemState);

        if (nested) {
            IframeWidget.closeModal();
            setShow(false);
            return;
        }

        if (location.state?.fromVoucher) {
            IframeWidget.closeModal();
            setShow(false);
            handleNavigateFromModal();
        } else {
            IframeWidget.closeModal();
            setShow(false);

            if (location.state?.promotionId) {
                await handleRemovePromotion();
            }

            handleNavigateFromModal();
        }
    }

    const getModalTitle = () => {
        if (itemGroup) return itemGroup.translation ? itemGroup.translation.name : itemGroup.name;

        return item.translation.name;
    }

    const isSimpleView = () => {
        return !productView.description && !item.modifier_groups?.length && !item.selected_items?.length && !StoreUtils.isItemCommentAvailable(config) && !productView.content;
    };

    const handleUpdateItemState = (newItemState: Record<string, any>) => {
        const copiedItemState = JSON.parse(JSON.stringify(newItemState));
        StateItemHelper.refreshItemPrice(copiedItemState, PromotionHelper.getForItem(promotions, newItemState.item_id), cart);
        setItemState(copiedItemState);
    };

    return (
        <Modal dialogClassName={`modal-full product-modal ${isSimpleView() ? "product-modal--small" : ""}`}
               show={show}
               aria-labelledby="menu_product_modal" id="product-modal"
               onHide={handleHideModal}
               onShow={() => IframeWidget.onShow()}>
            <ResponsiveComponent small={
                <>
                    <MenuProductModalHeader onClick={handleHideModal} title={getModalTitle()}
                                            ariaLabelledBy="menu_product_modal"/>
                    <Modal.Body>
                        <ProductContent productState={itemState} product={item} displayImages
                                        productItemGroup={itemGroup}
                                        updateProductState={handleUpdateItemState}
                                        productView={productView} nested={nested} isProductAvailable={isItemAvailable}/>
                    </Modal.Body>
                    <MenuProductModalFooter productState={itemState} updateProductState={handleUpdateItemState}
                                            onSubmit={handleSubmit} productView={productView}
                                            isProductAvailable={isItemAvailable} errors={errors}
                                            simple={nested} product={item} setIsProductAvailable={setIsItemAvailable}
                                            loading={loading} productItemGroup={itemGroup}
                    />
                </>
            } large={
                isSimpleView() ? (
                    <>
                        <MenuProductModalHeader onClick={handleHideModal} title={getModalTitle()}
                                                ariaLabelledBy="menu_product_modal"/>
                        <Modal.Body>
                            <ProductContent productState={itemState} product={item} displayImages
                                            productItemGroup={itemGroup} isProductAvailable={isItemAvailable}
                                            updateProductState={handleUpdateItemState}
                                            productView={productView} nested={nested}/>
                        </Modal.Body>
                        <MenuProductModalFooter productState={itemState} updateProductState={handleUpdateItemState}
                                                onSubmit={handleSubmit} productView={productView}
                                                simple={nested} product={item} isProductAvailable={isItemAvailable}
                                                setIsProductAvailable={setIsItemAvailable} errors={errors}
                                                loading={loading} productItemGroup={itemGroup}
                        />
                    </>
                ) : (
                    <Modal.Body>
                        <div className="columns-container">
                            <div className="col-6 col-left">
                                <MenuProductImages product={item} isProductAvailable={isItemAvailable}/>
                            </div>
                            <div className="col-6 col-right">
                                <MenuProductModalHeader onClick={handleHideModal}
                                                        title={getModalTitle()}
                                                        ariaLabelledBy="menu_product_modal"/>
                                <ProductContent productState={itemState} productItemGroup={itemGroup} product={item}
                                                updateProductState={handleUpdateItemState} productView={productView}
                                                nested={nested} isProductAvailable={isItemAvailable}/>
                                <MenuProductModalFooter productState={itemState} isProductAvailable={isItemAvailable}
                                                        simple={nested} onSubmit={handleSubmit}
                                                        productView={productView} product={item}
                                                        setIsProductAvailable={setIsItemAvailable}
                                                        updateProductState={handleUpdateItemState}
                                                        errors={errors} loading={loading} productItemGroup={itemGroup}
                                />
                            </div>
                        </div>
                    </Modal.Body>
                )
            } breakpoint={832}/>
        </Modal>
    )
}

const mapStateToProps = (state: RootState) => ({
    items: getItems(state.menu),
    session: getSession(state.session),
    itemGroups: getItemGroups(state.menu),
    modifierGroups: getModifierGroups(state.menu),
    promotions: getPromotions(state.menu),
    cart: getCart(state.cart),
    availabilities: getAvailabilities(state.menu),
})

const connector = connect(mapStateToProps);
export default connector(MenuProductModal);
